import * as Dialog from '@radix-ui/react-dialog';
import { FileText, Image as ImageIcon, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useEvidenceFile } from '@/lib/api/ops-queries';
import { shortId } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/feedback';

/**
 * A button that opens one deposit's uploaded slip, without leaving the screen.
 *
 * `CustomerUploadCard` shows the same file, but it is a card: it belongs on a screen that is
 * *about* that one deposit. This is for a list of several — the split-fill lineage, where an
 * operator is comparing the slips of sibling deposits that filled the same withdrawal, and
 * opening each in turn on its own page would lose the comparison they came for.
 *
 * The file is fetched only once the dialog opens. `Dialog.Portal` mounts nothing while closed,
 * so the query inside the body never runs for the rows an operator did not ask about — a lineage
 * lists several deposits and each slip is a whole image, streamed as bytes rather than a URL
 * (see `EvidenceStore.getObject`), so eagerly loading them all would be several megabytes to
 * render a row of buttons.
 */

/** Props for {@link SlipPreviewButton}. */
export interface SlipPreviewButtonProps {
  /** The deposit whose uploaded file to show. */
  readonly transactionId: string;
}

/** Open one deposit's uploaded slip in a dialog. */
export function SlipPreviewButton({ transactionId }: SlipPreviewButtonProps): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button variant="outline" size="sm" className="h-6 gap-1 px-2 text-2xs [&_svg]:size-3">
          <ImageIcon aria-hidden="true" />
          {t('domain.slipPreview.open')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/80" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[92vh] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 focus:outline-none">
          <Dialog.Title className="sr-only">
            {t('domain.slipPreview.title', { id: shortId(transactionId) })}
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            {t('domain.slipPreview.description')}
          </Dialog.Description>
          <SlipPreviewBody transactionId={transactionId} />
          <Dialog.Close asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('domain.slipPreview.close')}
              className="absolute right-2 top-2 bg-background/80 hover:bg-background"
            >
              <X aria-hidden="true" />
            </Button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * The dialog's contents — mounted only while it is open, which is what keeps the fetch lazy.
 *
 * "No file" is stated rather than rendered as an error: `has_slip_file` says an object was
 * stored, but the read can still come back empty when storage has since lost it, and an operator
 * who clicked expecting a slip is owed that sentence instead of an empty box.
 */
function SlipPreviewBody({ transactionId }: { readonly transactionId: string }): ReactNode {
  const { t } = useTranslation();
  const file = useEvidenceFile(transactionId);

  if (file.isLoading) {
    return (
      <div className="rounded-lg bg-background p-4" role="status">
        <Skeleton className="h-64 w-64" />
        <p className="mt-2 text-xs text-muted-foreground">{t('domain.slipPreview.loading')}</p>
      </div>
    );
  }
  if (!file.data) {
    return (
      <div className="rounded-lg bg-background p-6">
        <p className="text-sm text-muted-foreground">{t('domain.slipPreview.missing')}</p>
      </div>
    );
  }

  const { url, contentType } = file.data;
  if (contentType.startsWith('image/')) {
    return (
      <img
        src={url}
        alt={t('domain.slipPreview.title', { id: shortId(transactionId) })}
        className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain"
      />
    );
  }
  return (
    <div className="rounded-lg bg-background p-6">
      {contentType === 'application/pdf' ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm font-medium text-primary underline underline-offset-2"
        >
          <FileText className="size-4" aria-hidden="true" />
          {t('domain.slipPreview.pdfLink')}
        </a>
      ) : (
        <p className="text-sm text-muted-foreground">{t('domain.slipPreview.noPreview')}</p>
      )}
    </div>
  );
}
